import { z } from "zod";
import {
  buildStackedBarData,
  buildStackedChartConfig,
  sortSeriesBySentimentOrder,
} from "@/components/ui/bar-chart/ChatBarChartSpec";
import type { ChartConfig } from "@/components/ui/ChartTooltip";
import ComposedChartView, {
  type ComposedChartSeriesItem,
} from "@/components/ui/composed-chart/ComposedChartView";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

export const ChatComposedChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    xKey: z.string(),
    series: z.array(
      z.object({
        key: z.string(),
        type: z.enum(["bar", "line", "area"]),
        label: z.string().nullable().optional(),
        color: chartHexColorSchema.nullable().optional(),
      }),
    ),
    aggregate: z.enum(["sum", "count", "avg"]).nullable().optional(),
    height: z.number().nullable().optional(),
  }),
  description:
    'Mixed chart: bars, lines, and/or areas on the same category axis. Props: title (string or null), data, xKey, series (array of { key, type: "bar" | "line" | "area", label, color? }, where color is optional #RGB or #RRGGBB), aggregate ("sum" | "count" | "avg" or null), height (number or null). Keys positive / negative / neutral (case-insensitive) get sentiment colors; others var(--chart-1)–var(--chart-5) by index when color is omitted. Use { "$state": "/path" } to bind read-only data.',
  example: {
    title: "Volume vs. sentiment ratio",
    data: [
      { week: "Jan 6–12", articles: 186, ratio: 0.42 },
      { week: "Jan 13–18", articles: 305, ratio: 0.51 },
      { week: "Jan 20–26", articles: 237, ratio: 0.38 },
    ],
    xKey: "week",
    series: [
      { key: "articles", type: "bar", label: "Articles" },
      { key: "ratio", type: "line", label: "Positive ratio" },
    ],
    aggregate: null,
    height: 280,
  },
} as const;

/** Parses props and renders {@link ComposedChartView}. */
export const ChatComposedChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatComposedChartSpec.props.safeParse(props);
  if (!parsed.success) return null;

  const {
    title,
    data,
    xKey,
    series: seriesSpec,
    aggregate,
    height,
  } = parsed.data;
  const sortedSeries = sortSeriesBySentimentOrder(seriesSpec);
  const seriesKeys = sortedSeries.map((s) => s.key);
  const chartData = buildStackedBarData({
    data,
    xKey,
    seriesKeys,
    aggregate: aggregate ?? null,
  });
  const chartConfig: ChartConfig = buildStackedChartConfig(
    sortedSeries.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
    })),
  );

  const series: ComposedChartSeriesItem[] = sortedSeries.map((s) => ({
    key: s.key,
    type: s.type,
    label: s.label ?? undefined,
  }));

  return (
    <ComposedChartView
      title={title ?? null}
      data={chartData}
      chartConfig={chartConfig}
      xAxisKey={xKey}
      series={series}
      height={height ?? null}
      isAnimationActive={false}
    />
  );
};
